import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './css/index.module.css';
import { GetDataroomItemInfo, GetWorkstreams } from '../../redux/dataroomSlice';
import { AddBulkInformationRequests, GetDefaultQuestions, PostInformationRequests, UploadCSVFile, DownloadCSVTemplate } from '../../redux/questionBankSlice';
import { WorkstreamSelect } from '../Observations';
import { GetIRLDoc } from '../../redux/documentsSlice';
import { IoIosAdd } from 'react-icons/io';
import { Offcanvas } from 'react-bootstrap';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { useNavigate } from 'react-router';
import { Dropdown } from 'react-bootstrap';
import { BsChevronDown } from 'react-icons/bs';
import { PostWorkstream } from '../../redux/dataroomSlice';
import { toast } from 'react-toastify';

const parseCsvHeader = line => {
    const headers = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        const next = line[i + 1];
        if (ch === '"' && inQuotes && next === '"') {
            current += '"';
            i += 1;
            continue;
        }
        if (ch === '"') {
            inQuotes = !inQuotes;
            continue;
        }
        if (ch === ',' && !inQuotes) {
            headers.push(current.trim());
            current = '';
            continue;
        }
        current += ch;
    }
    headers.push(current.trim());
    return headers.filter(Boolean);
};

const defaultCsvMapping = () => ({
    question: '',
    tag: '',
    response: '',
});

const QuestionBank = () => {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state.projectData);
    const { workstreams } = useSelector(state => state.dataroom);
    const { questionBank, addtoIRLBtnLoading, uploadCSVBtnLoading, downloadTemplateLoading } = useSelector(state => state.questionBank);
    const { user } = useSelector(state => state.user);
    const isProjectAdmin = selectedProject?.vdr?.vdr_admin === user?.id || selectedProject?.project_admins?.some(admin => admin.user_id === user?.id);
    const { projectUsersAll } = useSelector(state => state.team);
    const [selectedWorkstream, setSelectedWorkstream] = React.useState();
    const [selectedTag, setSelectedTag] = React.useState('');
    const [selectedRows, setSelectedRows] = React.useState([]);
    const [filteredData, setFilteredData] = React.useState([]);
    const [showModal, setShowModal] = React.useState(false);
    const [selectedQuestion, setSelectedQuestion] = React.useState();
    const [availableTags, setAvailableTags] = React.useState([]);
    const navigate = useNavigate();
    const hiddenFileInput = React.useRef(null);
    const [uploadedQuestions, setUploadedQuestions] = React.useState([]);
    const [csvUploaded, setCsvUploaded] = React.useState(false);
    const [csvFileForMapping, setCsvFileForMapping] = React.useState(null);
    const [csvHeaders, setCsvHeaders] = React.useState([]);
    const [csvMapping, setCsvMapping] = React.useState(defaultCsvMapping);
    const hasResponseColumn = React.useMemo(
        () => uploadedQuestions?.some(item => Object.prototype.hasOwnProperty.call(item, 'response')),
        [uploadedQuestions],
    );

    const applyUploadedCsvResponse = response => {
        if (response.payload?.data) {
            setUploadedQuestions(response.payload.data);
            setCsvUploaded(true);
            setSelectedTag('');
        }
    };

    const handleUploadFileDirectly = async file => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await dispatch(UploadCSVFile(formData));
        applyUploadedCsvResponse(response);
    };

    const handleUploadCSV = async e => {
        const file = e.target.files[0];
        if (!file) {
            return;
        }

        const fileName = file.name?.toLowerCase() || '';

        // Excel files upload directly; CSV opens the column-mapping modal first.
        if (!fileName.endsWith('.csv')) {
            await handleUploadFileDirectly(file);
            e.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = readEvent => {
            try {
                const text = (readEvent.target?.result || '').toString();
                const firstNonEmptyLine = text.split(/\r?\n/).find(line => line.trim().length > 0) || '';
                if (!firstNonEmptyLine) {
                    toast.error('No headers found in CSV file.');
                    return;
                }

                const headers = parseCsvHeader(firstNonEmptyLine);
                if (!headers.length) {
                    toast.error('No headers found in CSV file.');
                    return;
                }

                const normalizedHeaders = headers.map(h => (h || '').toString().trim());
                const fieldAliases = {
                    question: ['question', 'questions', 'request', 'irl question', 'information request'],
                    tag: ['tag', 'tags', 'folder', 'category', 'categories'],
                    response: ['response', 'answer', 'respond', 'reply'],
                };

                const guessedMapping = defaultCsvMapping();
                const findBestMatch = aliases => {
                    const loweredAliases = aliases.map(a => a.toLowerCase());
                    for (const header of normalizedHeaders) {
                        if (loweredAliases.includes(header.toLowerCase())) return header;
                    }
                    for (const header of normalizedHeaders) {
                        const h = header.toLowerCase();
                        if (loweredAliases.some(a => h.includes(a))) return header;
                    }
                    return '';
                };

                Object.keys(fieldAliases).forEach(fieldKey => {
                    const match = findBestMatch(fieldAliases[fieldKey]);
                    if (match) guessedMapping[fieldKey] = match;
                });

                setCsvFileForMapping(file);
                setCsvHeaders(headers);
                setCsvMapping({
                    ...guessedMapping,
                    question: guessedMapping.question || headers[0] || '',
                });
            } catch (err) {
                console.error('Failed to read CSV file:', err);
                toast.error('Failed to read CSV file. Please check the file format.');
            }
        };
        reader.onerror = () => toast.error('Failed to read CSV file. Please check the file format.');
        reader.readAsText(file);
        e.target.value = '';
    };

    const handleChangeCsvMapping = (fieldKey, headerName) => {
        setCsvMapping(prev => ({ ...prev, [fieldKey]: headerName }));
    };

    const handleCancelCsvImport = () => {
        setCsvFileForMapping(null);
        setCsvHeaders([]);
        setCsvMapping(defaultCsvMapping());
    };

    const handleConfirmCsvImport = async () => {
        if (!csvFileForMapping) return;
        if (!csvMapping.question || !csvMapping.tag) {
            toast.error('Please map Question and Tag columns before importing.');
            return;
        }

        const formData = new FormData();
        formData.append('file', csvFileForMapping);
        formData.append('column_mapping', JSON.stringify(csvMapping));
        const response = await dispatch(UploadCSVFile(formData));
        applyUploadedCsvResponse(response);
        handleCancelCsvImport();
    };

    const handleDownloadTemplate = async () => {
        await dispatch(DownloadCSVTemplate());
    };

    const handleSelectUploadedRows = index => {
        if (selectedRows?.includes(index)) {
            setSelectedRows(selectedRows.filter(x => x !== index));
        } else {
            setSelectedRows([...selectedRows, index]);
        }
    };

    const handleSelectAllUploaded = val => {
        if (val) {
            setSelectedRows(uploadedQuestions.map((_, index) => index));
        } else {
            setSelectedRows([]);
        }
    };

    const handleSelectAllFilter = val => {
        if (val) {
            let ids = [];
            filteredData?.map(tab => {
                return ids.push(tab?.id);
            });
            setSelectedRows(ids);
        } else setSelectedRows([]);
    };

    const handleSelectRows = id => {
        if (selectedRows?.find(x => x === id)) {
            setSelectedRows(selectedRows.filter(x => x !== id));
        } else {
            setSelectedRows([...selectedRows, id]);
        }
    };

    const handleTagClick = tag => {
        if (selectedTag === tag) {
            // Deselect tag
            setSelectedTag('');
        } else {
            // Select tag
            setSelectedTag(tag);
        }
    };

    const handleAddMultiple = async () => {
        let data = selectedRows?.map(row => {
            let stockReq = filteredData?.find(item => item?.id === row);
            let obj = {
                request: stockReq?.question,
                vdr: selectedProject?.vdr?.id,
                tag: stockReq?.tag,
                workstream: selectedWorkstream?.id,
                priorities: 'NONE',
                request_class: 'Custom',
                added_by: projectUsersAll?.find(item => item?.value?.user_id === user?.id)?.value?.group_member_id,
            };
            if (!obj?.added_by && isProjectAdmin) {
                obj.added_by_offline = 'Project Admin';
                delete obj.added_by;
            }
            return obj;
        });
        const response = await dispatch(AddBulkInformationRequests({ items: data }));
        if (response.payload) {
            setSelectedRows([]);
            setSelectedQuestion();
            navigate('/irl');
        }
    };

    const handleAddUploadedToIRL = async () => {
        let data = selectedRows?.map(rowId => {
            let question = filteredData?.find(item => item?.id === rowId);
            let obj = {
                request: question?.question,
                response: question?.response || '',
                vdr: selectedProject?.vdr?.id,
                tag: question?.tag,
                workstream: selectedWorkstream?.id,
                priorities: 'NONE',
                request_class: 'Custom',
                added_by: projectUsersAll?.find(item => item?.value?.user_id === user?.id)?.value?.group_member_id,
            };
            if (!obj?.added_by && isProjectAdmin) {
                obj.added_by_offline = 'Project Admin';
                delete obj.added_by;
            }
            return obj;
        });
        const response = await dispatch(AddBulkInformationRequests({ items: data }));
        if (response.payload) {
            setSelectedRows([]);
            navigate('/irl');
        }
    };

    React.useEffect(() => {
        if (selectedProject) {
            dispatch(GetWorkstreams());
        }
    }, [selectedProject]);

    React.useEffect(() => {
        if (workstreams?.length > 0) {
            if (localStorage['workstream']) {
                const wsFromLocal = workstreams?.find(item => item?.id === parseInt(localStorage['workstream']));
                if (wsFromLocal) setSelectedWorkstream(wsFromLocal);
            } else setSelectedWorkstream(workstreams[0]);
        }
    }, [workstreams]);

    React.useEffect(() => {
        if (selectedWorkstream) {
            dispatch(GetIRLDoc(selectedWorkstream?.id));
            dispatch(GetDataroomItemInfo(selectedWorkstream?.id));
            dispatch(GetDefaultQuestions(selectedWorkstream?.name));
            setSelectedRows([]);
            setSelectedQuestion();
            setSelectedTag('');
        }
    }, [selectedWorkstream]);

    React.useEffect(() => {
        // Only show tags from question bank, not from CSV
        if (questionBank && questionBank.length > 0) {
            const questionBankTags = [...new Set(questionBank.map(entry => entry.tag))];
            setAvailableTags(questionBankTags);
        } else {
            setAvailableTags([]);
        }
    }, [questionBank]);

    React.useEffect(() => {
        if (selectedTag) {
            // If tag is selected, show default questions for that tag
            setFilteredData(questionBank?.filter(item => item?.tag === selectedTag) || []);
        } else if (csvUploaded && uploadedQuestions.length > 0) {
            // If no tag selected but CSV is uploaded, show uploaded questions
            setFilteredData(
                uploadedQuestions.map((item, index) => ({
                    ...item,
                    id: `uploaded-${index}`,
                    question: item.question,
                })),
            );
        } else {
            // If no tag selected and no CSV uploaded, show nothing
            setFilteredData([]);
        }
    }, [selectedTag, questionBank, uploadedQuestions, csvUploaded]);

    return (
        <div>
            <div className={styles.headerSection}>
                <div className="d-flex align-items-center justify-content-between">
                    <h5 className={styles.headerTitle}>Stock Questions</h5>
                    <div>
                        <button
                            onClick={() => hiddenFileInput?.current?.click()}
                            type="button"
                            className={`btn btn-primary btn-sm add-irl-button ms-4 ${styles.smallButton}`}
                            disabled={!selectedWorkstream || uploadCSVBtnLoading}>
                            Upload From CSV {uploadCSVBtnLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                        </button>
                        <button onClick={handleDownloadTemplate} disabled={downloadTemplateLoading} className={`btn btn-primary btn-sm ms-2 ${styles.smallButton}`}>
                            Download Template {downloadTemplateLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                        </button>
                        <button onClick={() => navigate('/irl')} type="button" className={`btn btn-primary btn-sm ${styles.responseButton} ${styles.smallButton} ms-2`}>
                            Back
                        </button>
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12 col-md-2">
                        <WorkstreamSelect selectedOption={selectedWorkstream} setSelectedOption={setSelectedWorkstream} />
                    </div>
                </div>
            </div>
            <input type="file" style={{ display: 'none' }} onChange={handleUploadCSV} accept=".xlsx,.xls,.csv" ref={hiddenFileInput} />

            {selectedWorkstream && (availableTags?.length > 0 || csvUploaded) && (
                <>
                    {availableTags?.length > 0 && (
                        <div>
                            <p className={styles.selectTag}>Select Tag</p>
                            <div className="d-flex flex-wrap">
                                {availableTags?.map(item => (
                                    <div
                                        style={{
                                            background: selectedTag === item ? 'var(--info)' : 'transparent',
                                            color: selectedTag === item ? 'white' : 'var(--info)',
                                            borderColor: selectedTag === item ? 'var(--info)' : 'var(--info)',
                                        }}
                                        onClick={() => handleTagClick(item)}
                                        className={styles.tagChip}>
                                        {item}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {filteredData.length > 0 && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                                <b>{selectedRows?.length > 0 && `${selectedRows?.length} questions selected`}</b>
                                <button
                                    onClick={selectedTag ? handleAddMultiple : handleAddUploadedToIRL}
                                    type="button"
                                    className={`btn btn-primary btn-sm add-irl-button ms-4 ${styles.smallButton}`}
                                    disabled={selectedRows.length === 0}>
                                    Add to IRL {addtoIRLBtnLoading ? <AiOutlineLoading3Quarters className="rotateLoader" /> : <IoIosAdd size={16} />}
                                </button>
                            </div>
                            <div className={styles.tableHeader}>
                                <input
                                    type="checkbox"
                                    className="me-4"
                                    checked={selectedRows?.length === filteredData.length ? true : false}
                                    onChange={e => handleSelectAllFilter(e.target.checked)}
                                />
                                {!selectedTag && csvUploaded ? (
                                    <div className="d-flex justify-content-between w-100">
                                        <p className="m-0" style={{ width: hasResponseColumn ? '50%' : '70%' }}>
                                            Question
                                        </p>
                                        <p className="m-0" style={{ width: hasResponseColumn ? '25%' : '30%' }}>
                                            Tag
                                        </p>
                                        {hasResponseColumn && (
                                            <p className="m-0" style={{ width: '25%' }}>
                                                Response
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <p className="m-0">{selectedTag ? `${selectedTag} Questions` : 'Uploaded Questions'}</p>
                                )}
                            </div>
                            {filteredData?.map((item, index) => (
                                <div key={item.id || index} className={styles.tableRow}>
                                    <div className="me-4">
                                        <input
                                            className={styles.rowCheck}
                                            type="checkbox"
                                            style={{ display: selectedRows?.find(x => x === (item.id || index)) && 'block' }}
                                            checked={selectedRows?.find(x => x === (item.id || index))}
                                            onChange={e => handleSelectRows(item.id || index, e.target.checked)}
                                        />
                                    </div>
                                    {!selectedTag && csvUploaded ? (
                                        <div className="d-flex justify-content-between w-100">
                                            <p
                                                onClick={() => {
                                                    setSelectedQuestion(item);
                                                    setShowModal(true);
                                                }}
                                                className="m-0"
                                                style={{ width: hasResponseColumn ? '50%' : '70%', cursor: 'pointer' }}>
                                                {item?.question}
                                            </p>
                                            <p className="m-0" style={{ width: hasResponseColumn ? '25%' : '30%' }}>
                                                {item?.tag}
                                            </p>
                                            {hasResponseColumn && (
                                                <p className="m-0 text-truncate" style={{ width: '25%' }}>
                                                    {item?.response}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <p
                                            onClick={() => {
                                                setSelectedQuestion(item);
                                                setShowModal(true);
                                            }}
                                            className="m-0">
                                            {item?.question}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {csvFileForMapping && csvHeaders.length > 0 && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.4)' }} role="dialog" aria-modal="true">
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Map CSV columns to IRL fields</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCancelCsvImport} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-2" style={{ fontSize: 12, color: '#6c757d' }}>
                                    File: {csvFileForMapping.name}
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-sm mb-2">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40%' }}>IRL field</th>
                                                <th>CSV column</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { key: 'question', label: 'Question (required)' },
                                                { key: 'tag', label: 'Tag (required)' },
                                                { key: 'response', label: 'Response' },
                                            ].map(field => (
                                                <tr key={field.key}>
                                                    <td>{field.label}</td>
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={csvMapping[field.key] || ''}
                                                            onChange={e => handleChangeCsvMapping(field.key, e.target.value)}>
                                                            <option value="">Not mapped</option>
                                                            {csvHeaders.map(header => (
                                                                <option key={header} value={header}>
                                                                    {header}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCancelCsvImport}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary btn-sm" onClick={handleConfirmCsvImport} disabled={uploadCSVBtnLoading}>
                                    Import CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <AddFolderModal
                showModal={showModal}
                setShowModal={setShowModal}
                selectedQuestion={selectedQuestion}
                setSelectedQuestion={setSelectedQuestion}
                selectedWorkstream={selectedWorkstream}
            />
        </div>
    );
};

const AddFolderModal = ({ showModal, setShowModal, selectedQuestion, selectedWorkstream, setSelectedQuestion }) => {
    const { user } = useSelector(state => state.user);
    const { selectedProject } = useSelector(state => state.projectData);
    const isProjectAdmin = selectedProject?.vdr?.vdr_admin === user?.id || selectedProject?.project_admins?.some(admin => admin.user_id === user?.id);
    const { projectUsersAll } = useSelector(state => state.team);
    const { customRequestLoading } = useSelector(state => state.questionBank);
    const dispatch = useDispatch();
    const handleClose = () => {
        setShowModal(false);
        setSelectedQuestion();
    };

    const handleSubmit = async () => {
        let body = {
            request: selectedQuestion?.question,
            response: selectedQuestion?.response || '',
            vdr: selectedProject?.vdr?.id,
            tag: selectedQuestion?.tag,
            workstream: selectedWorkstream?.id,
            request_class: 'Custom',
            added_by: projectUsersAll?.find(item => item?.value?.user_id === user?.id)?.value?.group_member_id,
            priorities: 'NONE',
        };
        if (!body?.added_by && isProjectAdmin) {
            body.added_by_offline = 'Project Admin';
            delete body.added_by;
        }

        await dispatch(PostInformationRequests({ body }));
        handleClose();
    };
    return (
        <>
            <Offcanvas placement="end" show={showModal} onHide={handleClose}>
                <Offcanvas.Body className={styles.sidebarBody}>
                    <div className="p-3">
                        <h5 className={styles.wsTitle}>{selectedQuestion?.workstream}</h5>
                        <p className={styles.tag}>{selectedQuestion?.tag}</p>
                        <p className={styles.questionText}>{selectedQuestion?.question}</p>
                        {selectedQuestion?.response && <p className={styles.questionText}>{selectedQuestion?.response}</p>}
                        <button onClick={handleSubmit} type="button" className={`btn btn-primary btn-sm add-irl-button mt-4 ${styles.smallButton}`}>
                            Add to IRL &nbsp;{customRequestLoading ? <AiOutlineLoading3Quarters className="rotateLoader" /> : <IoIosAdd size={16} />}{' '}
                        </button>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default QuestionBank;
